/**
 * D17 — effective Pricing Anchor resolution, for the UI.
 *
 * ⚠ The old header read "config-only; the price resolver reads NEITHER input".
 * RETRACTED 2026-08-31 (the Option-B arithmetic flip): the resolver reads both,
 * through `effectiveAnchorOf` in packages/domain/src/pricing/resolvePrice.ts.
 * This function stays the UI's path because it answers one extra question the
 * engine does not care about — 產品自選 vs company default, for the chip — and
 * returns null when nothing is set so no chip is fabricated. The two are pinned
 * equal over every seeded product by `pricingAnchorDefault.test.ts`.
 *
 * A product FOLLOWS the company default from Price Setup › Cost Basis until
 * it selects its own (`Product.pricingAnchor`; null/absent = follow —
 * Melissa 2026-08-28 "each product can select / should be selective").
 */
import type { PricingAnchor, PricingDataset } from '@/types/api/pricing';
import type { Product } from '@/types/api/product';

export interface EffectivePricingAnchor {
  anchor: PricingAnchor;
  /** 'product' = the product's own pick (產品自選); 'company' = Price Setup default. */
  source: 'product' | 'company';
}

export function effectivePricingAnchor(
  product: Pick<Product, 'pricingAnchor'>,
  dataset: Pick<PricingDataset, 'pricingAnchorDefault'> | null | undefined
): EffectivePricingAnchor | null {
  if (product.pricingAnchor) return { anchor: product.pricingAnchor, source: 'product' };
  if (dataset?.pricingAnchorDefault) {
    return { anchor: dataset.pricingAnchorDefault, source: 'company' };
  }
  return null;
}
